<template>
    <div>
        <div v-if="editing">
            <edit-task :event="editedTask" />
        </div>
        <div v-else>
            <div v-if="loading">
                <loading></loading>
            </div>

            <div v-else>
                <b-alert variant="danger" :show="hasErrors">
                    <span v-html="errorMessage"></span>
                </b-alert>

                <!-- filter -->
                <b-row v-if="!hasErrors" class="mb-3">
                    <b-col md="4" class="my-1">
                        <todo-filter />
                    </b-col>
                    <b-col md="4" class="my-1"></b-col>
                    <b-col md="4" class="my-1">
                        <b-form-group class="mb-0">
                            <b-input-group size="sm">
                                <b-form-input
                                    v-model="filter"
                                    placeholder="Entrer la valeur recherchée"
                                />
                                <b-input-group-append>
                                    <b-button
                                        :disabled="!filter"
                                        @click="resetSearch"
                                    >
                                        Effacer
                                    </b-button>
                                </b-input-group-append>
                            </b-input-group>
                        </b-form-group>
                    </b-col>
                </b-row>
                <!-- /filter -->

                <!-- Table -->
                <b-table
                    responsive
                    small
                    :items="filteredItems"
                    :fields="fields"
                    :filter="filter"
                    :per-page="perPage"
                    :current-page="currentPage"
                    @filtered="onFiltered"
                >
                    <template #cell(id)="data">
                        <item-menu :item="data.item"></item-menu>
                    </template>
                    <template #cell(priority)="data">
                        <priority-handler
                            :key="'ph' + data.item.id"
                            :priority="data.item.priority"
                        />
                    </template>
                    <template #cell(client)="data">
                        <span v-html="handleHomeRoute(data.item.client)"></span>
                    </template>
                    <template #cell(description)="data">
                        <b-link
                            title="Afficher les commentaires"
                            @click="commentTask(data.item)"
                        >
                            <strong>{{ data.item.description }}</strong>
                        </b-link>
                    </template>
                    <template #cell(budgeted_duration)="data">
                        <span>{{ data.item.budgeted_duration > 0 ? data.item.budgeted_duration.toFixed(2) + ' h' : '-' }}</span>
                    </template>
                    <template #cell(created_by)="data">
                        <span>{{ data.item.created_by.name }}</span>
                    </template>
                    <template #cell(created_at)="data">
                        <span>{{
                                data.item.created_at | moment('DD/MM/YYYY')
                            }}</span>
                    </template>
                    <template #cell(deadline_date)="data">
                        <span>{{
                                data.item.deadline_date | moment('DD/MM/YYYY')
                            }}</span>
                    </template>
                    <template #cell(billable)="data">
                        <billable-handler :status="data.item.billable" />
                    </template>
                    <template #cell(cb)="data">
                        <check-task
                            :cb="data.item.cb"
                            :done="data.item.done"
                            :can-complete="data.item.can_complete"
                        />
                    </template>
                </b-table>
                <!-- /Table -->

                <!-- Pagination -->
                <b-row>
                    <b-col md="3" class="my-1 text-center">
                        <b-pagination
                            v-if="totalRows > 0"
                            v-model="currentPage"
                            :total-rows="totalRows"
                            :per-page="perPage"
                            class="my-0"
                            size="sm"
                        ></b-pagination>
                    </b-col>
                    <b-col md="6" class="my-2">
                        <p class="text-center small pt-0">{{ legend }}</p>
                    </b-col>
                    <b-col md="3" class="my-1 text-right">
                        <b-dropdown
                            right
                            split
                            size="sm"
                            :text="perPage + ' éléments par page'"
                        >
                            <b-dropdown-item
                                v-for="pageOption in pageOptions"
                                :key="pageOption"
                                @click="setPerPage(pageOption)"
                            >
                                {{ pageOption }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </b-col>
                </b-row>
                <!-- /Pagination -->
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'

import TodoFilter from '../../activity/components/TodoFilter'
import loading from '../../../common/components/loading'
import CheckTask from '../../../common/components/CheckTask'
import ItemMenu from '../../../common/components/TaskMenu'
import EditTask from '../../clients/components/tasks/Edit'
import PriorityHandler from '../../../common/components/PriorityHandler'
import BillableHandler from '../../../common/components/BillableHandler'

import TableHandler from '../../../common/mixins/TableHandler'
import TodoFilterHandler from '../mixins/TodoFilterHandler'

export default {
    name: 'TableOwnTasks',

    components: {
        TodoFilter,
        loading,
        CheckTask,
        ItemMenu,
        EditTask,
        PriorityHandler,
        BillableHandler
    },

    mixins: [TableHandler, TodoFilterHandler],

    data() {
        return {
            fields: [
                {
                    key: 'id',
                    label: '',
                    class: 'td-sm',
                    sortable: false
                },
                {
                    key: 'priority',
                    label: 'Priorité',
                    class: 'td-sm text-center',
                    sortable: true
                },
                {
                    key: 'client',
                    label: 'Tiers',
                    class: 'td-sm td-bold',
                    sortable: true
                },
                {
                    key: 'description',
                    label: 'Tâche',
                    class: 'td-sm',
                    thStyle: 'width:40%',
                    sortable: true
                },
                {
                    key: 'budgeted_duration',
                    label: 'Budget',
                    class: 'td-sm text-right',
                    sortable: true
                },
                {
                    key: 'created_by',
                    label: 'Assignée par',
                    class: 'td-sm',
                    sortable: true
                },
                {
                    key: 'created_at',
                    label: 'Création',
                    class: 'td-sm text-center',
                    sortable: true
                },
                {
                    key: 'deadline_date',
                    label: 'Echéance',
                    class: 'td-sm text-center',
                    sortable: true
                },
                {
                    key: 'billable',
                    label: 'Facturable',
                    class: 'td-sm text-center',
                    sortable: true
                },
                {
                    key: 'cb',
                    label: 'Fait',
                    class: 'td-sm text-right',
                    sortable: false
                }
            ]
        }
    },

    async created() {
        if (!this.skipInit) {
            await this.init()
        }
    },

    methods: {
        async init() {
            this.$store.commit('browsing/RESET_EDITING')
            this.$store.commit('browsing/SET_PAGE_NAME', 'ownTasks')
            await this.getItems()
        },

        async getItems() {
            try {
                this.loading = true
                const response = await axios.get('/todo/own/tasks')
                this.items = response.data.data
                this.loading = false
                this.updateTotalRows()
            } catch (error) {
                console.log(error)
                this.handleErrors(error.response)
            }
        },

        commentTask(task) {
            this.$store.commit('data/SET_EDITED_TASK', task)
            this.$store.commit('browsing/SET_EDITING')
            this.$store.commit('browsing/SET_PAGE_NAME', 'tasks.edit')
        }
    },

    computed: {
        ...mapState({
            editing: (state) => state.browsing.editing,
            editedTask: (state) => state.data.editedTask,
            skipInit: (state) => state.browsing.init
        })
    }
}
</script>

<style scoped></style>
